import React from 'react';
import { Link } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { FiBarChart2, FiHome, FiUser, FiCalendar, FiArrowRight, FiTrendingUp } from 'react-icons/fi';

export default function AnalyticsIndex({ facilities = [], user }) {
  // Format percentage for display
  const formatPercentage = (value) => {
    return `${value}%`;
  };

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
                <p className="text-gray-500">Track performance metrics across all your facilities</p>
              </div>
            </div>
          </div>
          
          {/* Facilities Analytics Cards */}
          <div className="grid grid-cols-1 gap-6">
            {facilities.map((facility) => (
              <Card key={facility.id} className="shadow-sm hover:shadow transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>{facility.name}</CardTitle>
                    <Button variant="outline" size="sm" asChild>
                      <Link href={`/facility_owner/analytics/${facility.id}`}>
                        <FiBarChart2 className="mr-2 h-4 w-4" />
                        Detailed Analytics
                      </Link>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Occupancy Card */}
                    <div className="p-4 border rounded-md">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center">
                          <div className="p-2 rounded-full bg-blue-100 mr-2">
                            <FiHome className="h-4 w-4 text-blue-600" />
                          </div>
                          <h3 className="font-medium">Occupancy</h3>
                        </div>
                        <span className="text-lg font-bold">{formatPercentage(facility.availability.occupancy_rate)}</span>
                      </div>
                      <Progress 
                        value={facility.availability.occupancy_rate} 
                        className="h-2"
                      />
                      <div className="flex justify-between text-sm text-gray-500 mt-2">
                        <span>Total: {facility.availability.total_units}</span>
                        <span>Available: {facility.availability.available_units}</span>
                      </div>
                    </div>
                    
                    {/* Leads Card */}
                    <div className="p-4 border rounded-md">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center">
                          <div className="p-2 rounded-full bg-green-100 mr-2">
                            <FiUser className="h-4 w-4 text-green-600" />
                          </div>
                          <h3 className="font-medium">Leads</h3>
                        </div>
                        <span className="text-lg font-bold">{facility.leads_count}</span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-gray-500">Conversion Rate</span>
                        <div className="flex items-center">
                          <span className="font-medium mr-1">{formatPercentage(facility.conversion_metrics.lead_to_tour)}</span>
                          <FiTrendingUp className={`h-4 w-4 ${facility.conversion_metrics.lead_to_tour > 50 ? 'text-green-500' : 'text-amber-500'}`} />
                        </div>
                      </div>
                    </div>
                    
                    {/* Tours Card */}
                    <div className="p-4 border rounded-md">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center">
                          <div className="p-2 rounded-full bg-purple-100 mr-2">
                            <FiCalendar className="h-4 w-4 text-purple-600" />
                          </div>
                          <h3 className="font-medium">Tours</h3>
                        </div>
                        <span className="text-lg font-bold">{facility.tours_count}</span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-gray-500">Move-in Rate</span>
                        <div className="flex items-center">
                          <span className="font-medium mr-1">{formatPercentage(facility.conversion_metrics.tour_to_move_in)}</span>
                          <FiTrendingUp className={`h-4 w-4 ${facility.conversion_metrics.tour_to_move_in > 30 ? 'text-green-500' : 'text-amber-500'}`} />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end mt-4">
                    <Button variant="ghost" size="sm" asChild>
                      <Link href={`/facility_owner/facilities/${facility.id}`}>
                        View Facility <FiArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
